






















































































































import {
  defineComponent, reactive, toRefs, ref, onBeforeMount, PropType,
} from '@vue/composition-api'

import { getObjectDiff } from '@/lib/utils/getObjectDiff'
import {
  FormFields,
  FieldValue,
  isTextField,
  isSwitchField,
  isEditorField,
  isIsoDateField,
  isIsoDateValidationFn,
} from '@/components/types/FormFields'
import Editor from '@/components/appBase/Editor.vue'

type FieldValues = { [key: string]: FieldValue }

type State = {
  fieldValues: FieldValues;
  originalFieldValues: FieldValues;
  valid: boolean;
  anEditorFieldIsFocused: boolean;
  triggerEditorGetContent: boolean;
  myFields: FormFields;
}

const initialState: State = {
  fieldValues: {},
  originalFieldValues: {},
  valid: true,
  anEditorFieldIsFocused: false,
  triggerEditorGetContent: false,
  myFields: {},
}

export default defineComponent({
  components: {
    Editor,

  },
  props: {
    /**
     * Key/val object with the fields to display, with the shape
     * of Fields
     */
    fields: {
      type: Object as PropType<FormFields>,
      required: true,
    },
    /**
     * The parent can set this to true to display spinner e.g.
     * when values are sent to an api for updating.
     */
    processing: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, context) {
    const state = reactive({ ...initialState })

    // refs in the template (remember to also `return` them)
    // these are components that provide functionality (functions)
    const myFormRef = ref(null)

    // new try
    const editorFieldsGetContentFns: { [key: string]: () => string } = {}

    function cancel() {
      context.emit('cancel')
    }

    const setEditorFieldGetContentFn = (fieldName: string, cb: () => string) => {
      editorFieldsGetContentFns[fieldName] = cb
    }

    const getEditorFieldsContent = (cbObj: { [key: string]: () => string }) => {
      const content: { [key: string]: string } = {}
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, cb] of Object.entries(cbObj)) {
        content[key] = cb()
      }
      return content
    }

    /**
     * Returns a key/val object containing only the values that
     * have been changed in the form.
     */
    const getChanged = () => {
      const editorFieldValues = getEditorFieldsContent(editorFieldsGetContentFns)
      const currentValues = { ...state.fieldValues, ...editorFieldValues }
      return getObjectDiff({
        obj1: state.originalFieldValues,
        obj2: currentValues,
        limitToObj1Keys: true,
        includeCheckForMissingKeys: false,
      })
    }

    function formIsValid(): boolean {
      if (!myFormRef.value) return false
      const formRef = (myFormRef.value as unknown as & { validate: () => boolean })
      return formRef.validate()
    }

    /**
     * Emits 'save' event with a key/value obj of the values
     * that have been changed. If no values have changed it
     * emits 'cancel'.
     */
    const save = async () => {
      if (!formIsValid()) return
      const changed = getChanged()
      // console.log(changed)
      if (Object.keys(changed).length > 0) {
        context.emit('save', changed)
        return
      }
      context.emit('cancel')
    }

    // Call this method from the template on enter up in the form
    // The check allows for new lines in 'editor' fields without
    // triggering save()
    const onEnterKeyPressedInField = () => {
      if (!state.anEditorFieldIsFocused) save()
    }

    const dateToIso = (d: Date) => d.toISOString()

    const ISOStringToDate = (iso: string) => {
      const b = iso.split(/\D+/) as unknown as number[]
      // eslint-disable-next-line no-plusplus
      return new Date(Date.UTC(b[0], --b[1], b[2], b[3], b[4], b[5], b[6]))
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const updateIsoDateField = (name: unknown, ISOString: string) => {
      // const newDate
      // console.log('updating', name, ISOString)
      const newDate = ISOStringToDate(ISOString)
      // console.log('date', newDate)
      // state.fieldValues
    }

    // Copy field value to local state on mount (prevents updating field values
    // if the bound underlying data source changes while editing the input)
    onBeforeMount(() => {
      const obj: FieldValues = {}
      state.myFields = { ...props.fields }
      Object.keys(state.myFields).forEach((fieldName) => {
        let { value } = state.myFields[fieldName]
        // handle null in text fields
        if (state.myFields[fieldName].type === 'text' && value === null) {
          value = ''
        }
        obj[fieldName] = value
        // add date validation to isoDate fields
        // handle null in text fields
        if (state.myFields[fieldName].type === 'isoDate') {
          if (state.myFields[fieldName].validationFns !== undefined) {
            // eslint-disable-next-line no-unused-expressions
            state.myFields[fieldName].validationFns?.push(isIsoDateValidationFn())
          } else {
            state.myFields[fieldName].validationFns = [isIsoDateValidationFn()]
          }
        }
      })
      state.fieldValues = obj
      state.originalFieldValues = { ...obj } // copy, not reference
    })

    return {
      ...toRefs(state),
      cancel,
      onEnterKeyPressedInField,
      save,
      myFormRef,
      setEditorFieldGetContentFn,
      formIsValid,
      isTextField,
      isSwitchField,
      isEditorField,
      isIsoDateField,
      dateToIso,
      updateIsoDateField,
    }
  },
})

